import React, {
  forwardRef,
  useRef,
  useMemo,
  useCallback,
  useLayoutEffect,
  useEffect
} from 'react'
import PropTypes from 'prop-types'
import {
  findOptimalWrapperOffset,
  getFlattenedUnpackedSuggestions,
  getUnpackedSuggestions
} from './utils'
import {
  classNames,
  contains,
  useGlobalId,
  useSetState,
  useUpdateRef,
  elementAttributes
} from '@zambezi/sdk/common'
import { inputGhostRenderer, suggestionTextRenderer } from './renderers'
import LookupDropdown from './LookupDropdown'

/**
 * Create a React Lookup component
 *
 * @template T
 * @template Item
 * @param {import('./interfaces').LookupProps<T, Item>} props
 * @param {React.Ref<import('./interfaces').LookupRef>} ref
 * @returns {JSX.Element}
 */
function lookup (
  {
    className,
    dropdownClass,
    defaultValue,
    suggestions,
    disabledItemsIndices,
    getSuggestionValue,
    withGrouping,
    getGroupChildren,
    renderGroupLabel,
    renderInput,
    isError,
    error: propError = '',
    showErrorMessage,
    isDisabled,
    placeholder,
    autoFocus,
    tabIndex,
    title,
    id,
    inputProps,
    listProps,
    value: propsValue,
    onSuggestionSelected,
    shouldRenderNoResultsMessage,
    renderNoResultsMessage,
    shouldRenderSuggestions,
    suggestionsFetchRequested,
    suggestionsClearRequested,
    renderSuggestion,
    suggestionClassName,
    suggestionProps,
    onChange,
    onBlur,
    onFocus,
    onGroupLabelClick,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    enableVirtualScroll,
    withLabel,
    label,
    ...rest
  },
  ref
) {
  const unpackedSuggestions = useMemo(
    () =>
      getUnpackedSuggestions(
        suggestions,
        withGrouping,
        getGroupChildren,
        renderGroupLabel
      ),
    [suggestions, withGrouping, getGroupChildren, renderGroupLabel]
  )
  const flattenedSuggestions = useMemo(
    () => getFlattenedUnpackedSuggestions(withGrouping, unpackedSuggestions),
    [withGrouping, unpackedSuggestions]
  )
  const inputSuggestionValue = useMemo(
    () => getInputSuggestionValue(withGrouping, getSuggestionValue),
    [withGrouping, getSuggestionValue]
  )
  const canChangeValue = propsValue === undefined
  const [
    { activeIndex, value, isFocused, isOpen, focusedSuggestionValue },
    setState
  ] = useSetState({
    activeIndex: -1,
    value: (canChangeValue ? defaultValue : propsValue) || '',
    isFocused: false,
    focusedSuggestionValue: '',
    isOpen: false
  })
  const elementRef = useRef()
  const dropdownRef = useRef()
  const inputRef = useRef()
  const dropdownId = useGlobalId('zb-lookup-')
  const errorId = useGlobalId('zb-formidable-error-')
  const defaultInputId = useGlobalId('zb-input-')
  const defaultLabelId = useGlobalId('zb-input-label-')
  const activeDescendant =
    isOpen ? `${dropdownId}-${activeIndex}` : ''

  const handleClick = useCallback(
    (suggestion, suggestionIndex, event) => {
      inputRef.current && inputRef.current.focus()
      if (suggestionIndex === -1) return
      const value = getSuggestionValue(suggestion)
      setState({
        value: propsValue === undefined ? value : propsValue,
        focusedSuggestionValue: '',
        isOpen: false,
        activeIndex: suggestionIndex
      })
      onChange(event, { value, method: 'click' })
      onSuggestionSelected(event, {
        suggestion,
        suggestionIndex,
        method: 'click'
      })
      if (!shouldRenderSuggestions(value, event)) {
        suggestionsClearRequested()
      }
    },
    [
      getSuggestionValue,
      onChange,
      onSuggestionSelected,
      propsValue,
      shouldRenderSuggestions,
      suggestionsClearRequested
    ]
  )
  const handleHover = useCallback((suggestion, suggestionIndex = -1) => {
    setState({ activeIndex: suggestionIndex })
  }, [])

  const handleFocus = useCallback(
    event => {
      const isOpen = shouldRenderSuggestions(value, event)
      if (!isFocused) {
        setState({ isFocused: true, isOpen })
        if (isOpen) {
          suggestionsFetchRequested({ value, reason: 'input-focused' })
        } else {
          suggestionsClearRequested()
        }
        onFocus(event)
      }
    },
    [
      isFocused,
      onFocus,
      shouldRenderSuggestions,
      suggestionsClearRequested,
      suggestionsFetchRequested,
      value
    ]
  )

  const handleBlur = useCallback(
    event => {
      /**
       * event.relatedTarget is the element that will receive the focus,
       * however in IE it is the document.activeElement, relatedTarget is null.
       */
      const targetIsContained = contains(
        event.currentTarget,
        event.relatedTarget || document.activeElement
      )
      if (!targetIsContained) {
        if (isFocused) {
          setState({ isFocused: false, isOpen: false })
        }
        if (!shouldRenderSuggestions(value, event)) {
          suggestionsClearRequested()
        }
        onBlur(event)
      }
    },
    [
      isFocused,
      onBlur,
      shouldRenderSuggestions,
      suggestionsClearRequested,
      value
    ]
  )

  const handleChange = useCallback(
    event => {
      const { value } = event.target
      const shouldRender = shouldRenderSuggestions(value, event)
      const isOpen = shouldRender && isFocused

      setState({
        isOpen,
        value: propsValue === undefined ? value : propsValue,
        activeIndex: -1,
        isFocused: true,
        focusedSuggestionValue: ''
      })
      onChange(event, { value, method: 'type' })
      if (shouldRender) {
        suggestionsFetchRequested({ value, reason: 'input-changed' })
      } else {
        suggestionsClearRequested()
      }
    },
    [
      isFocused,
      onChange,
      propsValue,
      shouldRenderSuggestions,
      suggestionsClearRequested,
      suggestionsFetchRequested
    ]
  )

  const handleKeyUp = useCallback(
    event => {
      const { onKeyUp = noop } = inputProps
      switch (event.key) {
        case 'Escape':
        case 'Enter':
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault()
      }
      onKeyUp(event)
    },
    [inputProps]
  )

  useLayoutEffect(positionDropdown, [isOpen])
  useEffect(() => {
    const elementRefCurrent = elementRef?.current

    if (elementRefCurrent && autoFocus) {
      elementRefCurrent.focus()
    }
  }, [])
  useUpdateRef(ref, elementRef, { open, close, setFocusIndex, getFocusIndex })
  useEffect(componentWillReceiveProps, [propsValue])
  const disabledHandler = isDisabled ? null : true
  const wrapperProps = elementAttributes(rest, {
    className: classNames('zb-lookup', className, {
      'zb-lookup-is-open': isOpen
    }),
    onFocus: disabledHandler && handleFocus,
    onBlur: disabledHandler && handleBlur,
    ref: elementRef,
    tabIndex: autoFocus ? -1 : tabIndex
  })

  return (
    <div {...wrapperProps}>
      {withLabel && label
        ? (
          <span className='zb-input-label zb-input-label-sdk'>
            <label
              className='zb-input-label-name'
              id={inputProps['aria-labelledby'] || defaultLabelId}
              htmlFor={inputProps.id || id || defaultInputId}
            > {label}
            </label>
          </span>)
        : null}
      <div
        className={classNames('zb-lookup-input-wrapper', {
          'zb-lookup-input-wrapper-is-focused': isFocused,
          'zb-lookup-input-wrapper-is-error': isError,
          'zb-lookup-input-wrapper-is-disabled': isDisabled
        })}
      >
        {renderInput(getInputProps())}
      </div>
      <LookupDropdown
        className={dropdownClass}
        isOpen={isOpen}
        id={dropdownId}
        value={value}
        selected={activeIndex}
        suggestions={unpackedSuggestions}
        disabledItemsIndices={disabledItemsIndices}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        withGrouping={withGrouping}
        renderNoResultsMessage={
          shouldRenderNoResultsMessage(value, suggestions)
            ? renderNoResultsMessage
            : null
        }
        onGroupLabelClick={onGroupLabelClick}
        suggestionClassName={suggestionClassName}
        suggestionProps={suggestionProps}
        onClick={disabledHandler && handleClick}
        onHover={disabledHandler && handleHover}
        listProps={listProps}
        ref={dropdownRef}
        enableVirtualScroll={enableVirtualScroll}
      />
      {showErrorMessage && isError && propError
        ? (
          <span className='zb-error-message zb-error-message-sdk' id={errorId}>{propError}</span>
          )
        : null}
    </div>
  )

  function componentWillReceiveProps () {
    if (!canChangeValue) {
      setState({
        value: propsValue,
        activeIndex: -1,
        focusedSuggestionValue: ''
      })
    }
  }

  function handleKeyDown (event) {
    let newIndex
    const { onKeyDown = noop } = inputProps
    switch (event.key) {
      case 'Tab':
        setState({ isOpen: false, isFocused: false })
        break

      case 'ArrowDown':
        newIndex = getNewIndex(
          activeIndex,
          1,
          flattenedSuggestions.length,
          disabledItemsIndices
        )
        if (newIndex !== null) {
          const isOpen = shouldRenderSuggestions(value, event)
          setState({
            activeIndex: newIndex,
            focusedSuggestionValue: getFocusedSuggestionValue(newIndex),
            isOpen,
            isFocused: isOpen || isFocused
          })
          event.preventDefault()
        }
        break

      case 'ArrowUp':
        newIndex = getNewIndex(
          activeIndex,
          -1,
          flattenedSuggestions.length,
          disabledItemsIndices
        )
        if (newIndex !== null) {
          const isOpen = shouldRenderSuggestions(value, event)
          setState({
            activeIndex: newIndex,
            focusedSuggestionValue: getFocusedSuggestionValue(newIndex),
            isOpen,
            isFocused: isOpen || isFocused
          })
        }
        event.preventDefault()
        break

      case 'Escape': {
        const shouldRender = shouldRenderSuggestions('', event)
        if (isOpen) {
          const newValue = shouldRender ? value : ''

          setState({
            isOpen: false,
            focusedSuggestionValue: '',
            value: canChangeValue ? newValue : propsValue
          })
          if (value !== newValue) {
            onChange(event, { value: newValue, method: 'escape' })
          }

          if (shouldRender) {
            suggestionsFetchRequested({
              value: newValue,
              reason: 'escape-pressed'
            })
          } else {
            suggestionsClearRequested()
          }
        } else {
          setState({
            isOpen: false,
            focusedSuggestionValue: ''
          })
          if (shouldRender) {
            suggestionsFetchRequested({ value, reason: 'escape-pressed' })
          } else {
            suggestionsClearRequested()
          }
        }
        event.preventDefault()
        break
      }
      case 'Enter':
        if (isOpen) {
          if (activeIndex !== -1) {
            const value = getFocusedSuggestionValue(activeIndex)
            setState({
              value: canChangeValue ? value : propsValue,
              isOpen: false,
              focusedSuggestionValue: ''
            })
            onSuggestionSelected(event, {
              suggestion: flattenedSuggestions[activeIndex],
              suggestionIndex: activeIndex,
              method: 'enter'
            })
            if (!shouldRenderSuggestions('', event)) {
              suggestionsClearRequested()
            }
            onChange(event, { value, method: 'enter' })
          }
        } else {
          const shouldRender = shouldRenderSuggestions(value, event)
          setState({
            isOpen: shouldRender,
            isFocused: shouldRender || isFocused
          })
          if (shouldRender) {
            suggestionsFetchRequested({ value, reason: 'enter-pressed' })
          }
        }
        event.preventDefault()
        break
    }
    onKeyDown(event)
  }

  function getFocusedSuggestionValue (activeIndex) {
    return getSuggestionValue(flattenedSuggestions[activeIndex])
  }

  function setFocusIndex (activeIndex) {
    setState({ activeIndex })
  }

  function getFocusIndex () {
    return activeIndex
  }

  function close () {
    if (!isDisabled) {
      setState({ isOpen: false })
    }
  }

  function open () {
    if (!isDisabled) {
      setState({ isOpen: true, isFocused: true })
    }
  }

  function positionDropdown () {
    if (isOpen) {
      const element = elementRef.current
      const wrapperElement = dropdownRef.current
      const newOffset = findOptimalWrapperOffset(element, wrapperElement, {
        align: 'right',
        offset: 0
      })
      wrapperElement.style.right = newOffset + 'px'
    }
  }

  function getInputProps () {
    return Object.assign({}, inputProps, {
      placeholder,
      isFocused,
      isDisabled,
      isError,
      isOpen,
      suggestions: unpackedSuggestions,
      focusedSuggestionValue,
      getSuggestionValue: inputSuggestionValue,
      title,
      id,
      value,
      className: 'zb-lookup-input',
      type: 'text',
      onChange: handleChange,
      onKeyUp: disabledHandler && handleKeyUp,
      onKeyDown: disabledHandler && handleKeyDown,
      role: 'combobox',
      'aria-expanded': isOpen,
      'aria-haspopup': 'listbox',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy || defaultLabelId,
      'aria-autocomplete': 'list',
      'aria-controls': `${dropdownId}-list`,
      'aria-activedescendant': activeDescendant,
      'aria-invalid': !!isError,
      disabled: !!isDisabled,
      ref: inputRef,
      ...(isError && { 'aria-describedby': errorId })
    })
  }
}

/**
 * @param {number} activeIndex
 * @param {number} increment
 * @param {number} valuesLength
 * @param {number[]} disabledItemsIndices
 */
function getNewIndex (
  activeIndex,
  increment,
  valuesLength,
  disabledItemsIndices
) {
  if (disabledItemsIndices.length === valuesLength) return null

  const hasDisabledItems = !!disabledItemsIndices.length
  const step = Math.abs(increment)
  const newVal = increment < 0 ? valuesLength - 1 : 0
  const startIndex = activeIndex === -1 ? newVal : activeIndex + increment

  for (let i = 0; i < valuesLength; i += step) {
    const value = startIndex + i * increment
    const index = value < 0 ? valuesLength + value : value % valuesLength
    if (!hasDisabledItems || disabledItemsIndices.indexOf(index) === -1) {
      return index
    }
  }
  return activeIndex
}

/**
 * @template Item
 * @param {boolean} withGrouping
 * @param {import('./interfaces').SuggestionValueGetter<Item>} getSuggestionValue
 */
function getInputSuggestionValue (withGrouping, getSuggestionValue) {
  return withGrouping
    ? function (/** @type {{ group: Item[] }} */ { group }) {
      return getSuggestionValue(group[0])
    }
    : getSuggestionValue
}

function noop () {}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').LookupProps<T, Item> & React.RefAttributes<import('./interfaces').LookupRef>>}
 */
const Lookup = forwardRef(lookup)

Lookup.displayName = 'Lookup'

Lookup.propTypes = {
  autoFocus: PropTypes.bool,
  suggestions: PropTypes.array,
  disabledItemsIndices: PropTypes.arrayOf(PropTypes.number),
  renderSuggestion: PropTypes.func,
  suggestionClassName: PropTypes.func,
  suggestionProps: PropTypes.func,
  getSuggestionValue: PropTypes.func,
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  id: PropTypes.string,
  tabIndex: PropTypes.string,
  renderInput: PropTypes.func,
  renderNoResultsMessage: PropTypes.func,
  suggestionsFetchRequested: PropTypes.func,
  suggestionsClearRequested: PropTypes.func,
  shouldRenderSuggestions: PropTypes.func,
  shouldRenderNoResultsMessage: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  withGrouping: PropTypes.bool,
  renderGroupLabel: PropTypes.func,
  getGroupChildren: PropTypes.func,
  onGroupLabelClick: PropTypes.func,
  dropdownClass: PropTypes.string,
  enableVirtualScroll: PropTypes.bool,
  showErrorMessage: PropTypes.bool,
  error: PropTypes.any,
  withLabel: PropTypes.bool,
  label: PropTypes.string
}

Lookup.defaultProps = {
  autoFocus: false,
  tabIndex: 0,
  suggestions: [],
  disabledItemsIndices: [],
  inputProps: {},
  getSuggestionValue: String,
  renderGroupLabel: String,
  getGroupChildren: _ => [],
  suggestionClassName: noop,
  suggestionProps: () => ({}),
  renderSuggestion: suggestionTextRenderer,
  renderInput: inputGhostRenderer,
  renderNoResultsMessage: () => 'No matches found',
  onSuggestionSelected: noop,
  suggestionsFetchRequested: noop,
  suggestionsClearRequested: noop,
  shouldRenderSuggestions: val => val.length > 0,
  shouldRenderNoResultsMessage: (val, suggestions) =>
    suggestions.length === 0 && val.length !== 0,
  onChange: noop,
  onBlur: noop,
  onFocus: noop,
  dropdownClass: 'zb-select-dropdown-list',
  enableVirtualScroll: false,
  showErrorMessage: false
}

export default Lookup
